import { useRef, useCallback, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { SurveyInstance } from '@/types/survey.types';
import { MOCK_DASHBOARD } from '@/data/mockSurveys';
import { usePendingInstances, useDashboardStats, useCurrentScore, useSubmitResponse } from '@/hooks/usePendingInstances';
import { HomeHero } from './components/HomeHero';
import { ActiveSurveyInline } from './components/ActiveSurveyInline';
import { HomeKpiStrip } from './components/HomeKpiStrip';
import { GrowthBanner } from './components/GrowthBanner';
import { HomeTips } from './components/HomeTips';
import { useSurveyQuiz } from './hooks/useSurveyQuiz';
import './SurveyView.css';

interface SurveyViewProps {
  onNavigateToGrowth?: () => void;
}

// ─── Inner component (receives pre-loaded instances) ─────────────────────────

interface SurveyViewLoadedProps extends SurveyViewProps {
  instances: SurveyInstance[];
  stats: typeof MOCK_DASHBOARD.stats;
  currentScore: number;
}

type ToastState = { type: 'success' | 'error'; msg: string } | null;

function SurveyViewLoaded({ instances, stats, currentScore, onNavigateToGrowth }: SurveyViewLoadedProps) {
  const { t } = useTranslation();
  const { pending, quiz, omitSurvey, continueAfterComplete } = useSurveyQuiz(instances);
  const { mutate: submitResponse } = useSubmitResponse();
  const [toast, setToast] = useState<ToastState>(null);

  const initialTotal = useRef(instances.length);
  const surveysAnswered = initialTotal.current - pending.length;
  const currentInstance = quiz?.instance ?? null;

  // Auto-dismiss toast after 3 s
  useEffect(() => {
    if (!toast) return;
    const id = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(id);
  }, [toast]);

  const handleComplete = useCallback((answers: Record<string, string | number>) => {
    if (currentInstance) {
      submitResponse(
        { instanceId: currentInstance.id, answers, questions: currentInstance.questions },
        {
          onSuccess: () => setToast({ type: 'success', msg: t('survey.submitSuccess', 'Respuestas enviadas') }),
          onError:   () => setToast({ type: 'error',   msg: t('survey.submitError',   'Error al enviar — intenta de nuevo') }),
        },
      );
    }
    continueAfterComplete();
  }, [currentInstance, submitResponse, continueAfterComplete, t]);

  const handleOmit = useCallback(() => {
    omitSurvey({
      onSuccess: () => setToast({ type: 'success', msg: t('survey.omitSuccess', 'Encuesta omitida') }),
      onError:   () => setToast({ type: 'error',   msg: t('survey.omitError',   'Error al omitir — intenta de nuevo') }),
    });
  }, [omitSurvey, t]);

  return (
    <div className="survey-view">
      <HomeHero pendingCount={pending.length} />

      {currentInstance ? (
        <ActiveSurveyInline
          key={currentInstance.id}
          instance={currentInstance}
          totalAnswered={surveysAnswered}
          totalSurveys={initialTotal.current}
          stackCount={pending.length - 1}
          onOmit={handleOmit}
          onComplete={handleComplete}
          hasNext={pending.length > 1}
        />
      ) : (
        <div className="home-allclear">
          <img src="/assets/persona-asomada.svg" alt="" style={{ height: 64, opacity: 0.8 }} />
          <div>
            <div className="home-allclear-title">{t('survey.allDoneTitle')}</div>
            <div className="home-allclear-sub">{t('survey.allDoneSubtitle')}</div>
          </div>
        </div>
      )}

      <HomeKpiStrip stats={stats} />
      <GrowthBanner onGrowthClick={onNavigateToGrowth} />
      <HomeTips currentScore={currentScore} />

      {toast && (
        <div className={`sv-toast ${toast.type}`} role="status" aria-live="polite">
          <i className={`fas fa-${toast.type === 'success' ? 'check-circle' : 'exclamation-circle'}`} />
          {toast.msg}
        </div>
      )}
    </div>
  );
}

// ─── Shell: fetches data, shows loading skeleton while pending ────────────────

export function SurveyView({ onNavigateToGrowth }: SurveyViewProps) {
  const { t } = useTranslation();
  const { data: instances, isLoading: loadingInst } = usePendingInstances();
  const { data: stats, isLoading: loadingStats } = useDashboardStats();
  const { data: currentScore, isLoading: loadingScore } = useCurrentScore();

  if (loadingInst || loadingStats || loadingScore) {
    return (
      <div className="survey-view">
        <div className="home-loading" aria-busy="true">
          <div className="home-loading-spinner">
            <img src="/favicon.png" alt="" />
          </div>
          <span className="home-loading-text">{t('common.loading')}</span>
        </div>
      </div>
    );
  }

  return (
    <SurveyViewLoaded
      instances={instances ?? []}
      stats={stats ?? MOCK_DASHBOARD.stats}
      currentScore={currentScore ?? MOCK_DASHBOARD.currentScore}
      onNavigateToGrowth={onNavigateToGrowth}
    />
  );
}
